import { useLayoutEffect } from "react";

/**
 * Generic mechanism for a full-screen surface to hide the app shell's floating
 * chrome while it is open (route-backed section sheets, wizards, overlays…):
 * the mobile bottom nav and the chat launcher (`UnifiedChatWidget`). The names
 * predate the launcher; the flag covers both.
 *
 * Any number of surfaces may ask at once; the chrome reappears when the last
 * one unmounts. `document.documentElement[data-hide-mobile-nav]` mirrors the
 * state so CSS and end-to-end tests can observe it without importing this module.
 */
let requests = 0;
const listeners = new Set();

function emit() {
  const hidden = requests > 0;
  if (typeof document !== "undefined") {
    if (hidden) {
      document.documentElement.setAttribute("data-hide-mobile-nav", "true");
    } else {
      document.documentElement.removeAttribute("data-hide-mobile-nav");
    }
  }
  listeners.forEach((listener) => listener());
}

export function subscribeMobileNavHidden(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isMobileNavHidden() {
  return requests > 0;
}

/**
 * Hide the mobile bottom nav and the chat launcher while `active` and
 * this component is mounted.
 */
export function useHideMobileNav(active = true) {
  // Layout effect: the chrome is gone before the surface's first paint.
  useLayoutEffect(() => {
    if (!active) return undefined;
    requests += 1;
    emit();
    return () => {
      requests = Math.max(0, requests - 1);
      emit();
    };
  }, [active]);
}
